import { posthog } from 'posthog-js';
import type { CaptureResult } from 'posthog-js';
import { ZodError } from 'zod';
import {
  DEFAULT_ANALYTICS_ENVIRONMENT,
  DEFAULT_POSTHOG_HOST,
  DEFAULT_POSTHOG_UI_HOST,
  POSTHOG_SDK_DEFAULTS,
  stripUndefinedValues,
  type AnalyticsErrorCategory,
} from '../../shared/analytics.js';
import {
  classifyAppErrorCategory,
  extractAppErrorEnvelope,
  normalizeAppErrorReport,
  type AppErrorContext,
  type AppErrorReport,
} from '../../shared/app-errors.js';
import { buildClientExceptionFingerprint } from './errorFingerprint.js';

export type ClientPostHogEnv = {
  DEV?: boolean;
  MODE?: string;
  VITE_PUBLIC_ENVIRONMENT?: string;
  VITE_PUBLIC_POSTHOG_HOST?: string;
  VITE_PUBLIC_POSTHOG_KEY?: string;
};

type CaptureClientErrorOptions = {
  extras?: Record<string, unknown>;
  tags?: Record<string, string | number | boolean | undefined>;
};

export type ClientPostHogSettings = {
  defaults: typeof POSTHOG_SDK_DEFAULTS;
  enabled: boolean;
  environment: string;
  host: string;
  key?: string;
  uiHost: string;
};

const AUTH_REQUIRED_MESSAGE = 'Authentication required';
const CHUNK_LOAD_FAILURE_MESSAGE_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Unable to preload CSS for/i;
const RAW_BROWSER_NETWORK_FAILURE_MESSAGES = new Set([
  'Failed to fetch',
  'NetworkError when attempting to fetch resource.',
  'Load failed',
]);
const STRUCTURED_EXCEPTION_CONTEXT_KEYS = [
  'activation_milestone',
  'array_candidate_path_count',
  'candidate_path_count',
  'code',
  'collection_name',
  'config_id',
  'created_field_count',
  'details',
  'diagnostic_code',
  'error_category',
  'failed_field_count',
  'failure_phase',
  'field_id',
  'has_post_body',
  'http_status',
  'is_retryable',
  'missing_field_count',
  'missing_field_names',
  'network_error_message',
  'network_error_name',
  'plan',
  'preview_field_count',
  'preview_record_count',
  'project_id',
  'provider',
  'request_duration_ms',
  'request_id',
  'request_method',
  'request_path',
  'request_query_keys',
  'request_url',
  'requested_field_count',
  'resolved_items_path',
  'response_body_kind',
  'response_body_preview',
  'response_content_type',
  'response_shape',
  'run_id',
  'source_kind',
  'step',
  'surface',
  'trigger_source',
  'updated_field_type_count',
  'user_visible_message',
] as const;
const URL_IN_TEXT_PATTERN = /https?:\/\/[^\s'")]+|\/assets\/[^\s'")]+/i;

let isPostHogInitialized = false;

function normalizeString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function resolveClientAnalyticsEnvironment(env: ClientPostHogEnv): string {
  return (
    normalizeString(env.VITE_PUBLIC_ENVIRONMENT) ??
    normalizeString(env.MODE) ??
    (env.DEV === true ? 'development' : DEFAULT_ANALYTICS_ENVIRONMENT)
  );
}

function toError(error: unknown, fallback = 'Unknown error'): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    return new Error(error.message || fallback);
  }

  if (typeof error === 'string' && error.trim().length > 0) {
    return new Error(error);
  }

  try {
    return new Error(JSON.stringify(error) || fallback);
  } catch {
    return new Error(fallback);
  }
}

function isAbortLikeError(error: unknown): boolean {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    return error.name === 'AbortError';
  }

  return error instanceof Error && error.name === 'AbortError';
}

function isExpectedValidationError(error: unknown): boolean {
  if (error instanceof ZodError) {
    return true;
  }

  return false;
}

function isExpectedNetworkNoise(error: unknown): boolean {
  if (isAbortLikeError(error)) {
    return true;
  }

  return error instanceof TypeError && error.message === 'Failed to fetch';
}

function getErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  return normalizeString((error as { code?: unknown }).code);
}

function getErrorDetails(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  return normalizeString((error as { details?: unknown }).details);
}

function getErrorRequestId(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  return normalizeString((error as { requestId?: unknown }).requestId);
}

function getErrorRetryable(error: unknown): boolean | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const retryable = (error as { retryable?: unknown }).retryable;
  return typeof retryable === 'boolean' ? retryable : undefined;
}

function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

function getAppErrorEnvelope(error: unknown) {
  return error instanceof Error && 'payload' in error
    ? extractAppErrorEnvelope((error as { payload?: unknown }).payload)
    : null;
}

function getErrorNumber(error: unknown, key: string): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const value = (error as Record<string, unknown>)[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function getErrorString(error: unknown, key: string): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  return normalizeString((error as Record<string, unknown>)[key]);
}

function getErrorStringArray(error: unknown, key: string): string[] | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const value = (error as Record<string, unknown>)[key];
  if (!Array.isArray(value)) {
    return undefined;
  }

  const normalized = value.filter((entry): entry is string => typeof entry === 'string');
  return normalized.length > 0 ? normalized : undefined;
}

function getFetchJsonErrorCategory(error: unknown): AnalyticsErrorCategory | undefined {
  const code = getErrorCode(error);
  if (code === 'request_timeout') {
    return 'timeout';
  }
  if (code === 'network_request_failed') {
    return 'network';
  }

  const status = getErrorStatus(error);
  if (status === 408 || status === 504) {
    return 'timeout';
  }
  if (status === 401) {
    return 'auth';
  }
  if (status === 403) {
    return 'permission';
  }
  if (status === 404) {
    return 'not_found';
  }
  if (status === 409) {
    return 'conflict';
  }
  if (status === 429) {
    return 'rate_limited';
  }
  if (status === 0) {
    return 'network';
  }

  return undefined;
}

function isExpectedAuthRequiredError(error: unknown): boolean {
  return (
    getErrorStatus(error) === 401 &&
    (getErrorCode(error) === 'auth_required' ||
      (error instanceof Error && error.message === AUTH_REQUIRED_MESSAGE))
  );
}

function shouldSkipErrorCapture(error: unknown): boolean {
  return (
    isExpectedNetworkNoise(error) ||
    isExpectedValidationError(error) ||
    isExpectedAuthRequiredError(error)
  );
}

function stringValues(value: unknown): string[] {
  if (typeof value === 'string') {
    return [value];
  }

  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((entry): entry is string => typeof entry === 'string');
}

function exceptionListValues(properties: Record<string, unknown>, key: 'type' | 'value'): string[] {
  const list = properties.$exception_list;
  if (!Array.isArray(list)) {
    return [];
  }

  return list.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }

    const value = (entry as Record<string, unknown>)[key];
    return typeof value === 'string' ? [value] : [];
  });
}

function exceptionTypes(properties: Record<string, unknown>): string[] {
  return [
    ...stringValues(properties.$exception_type),
    ...stringValues(properties.$exception_types),
    ...exceptionListValues(properties, 'type'),
  ];
}

function exceptionValues(properties: Record<string, unknown>): string[] {
  return [
    ...stringValues(properties.$exception_value),
    ...stringValues(properties.$exception_values),
    ...exceptionListValues(properties, 'value'),
  ];
}

function exceptionFrameValues(properties: Record<string, unknown>): string[] {
  const list = properties.$exception_list;
  if (!Array.isArray(list)) {
    return [];
  }

  return list.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }

    const stacktrace = (entry as { stacktrace?: unknown }).stacktrace;
    if (!stacktrace || typeof stacktrace !== 'object') {
      return [];
    }

    const frames = (stacktrace as { frames?: unknown }).frames;
    if (!Array.isArray(frames)) {
      return [];
    }

    return frames.flatMap((frame) => {
      if (!frame || typeof frame !== 'object') {
        return [];
      }

      const record = frame as Record<string, unknown>;
      const rawFrame =
        record.junk_drawer &&
        typeof record.junk_drawer === 'object' &&
        'raw_frame' in record.junk_drawer &&
        (record.junk_drawer as { raw_frame?: unknown }).raw_frame &&
        typeof (record.junk_drawer as { raw_frame?: unknown }).raw_frame === 'object'
          ? (record.junk_drawer as { raw_frame: Record<string, unknown> }).raw_frame
          : null;

      return [
        record.source,
        record.mangled_name,
        record.resolved_name,
        rawFrame?.filename,
        rawFrame?.function,
      ].filter((value): value is string => typeof value === 'string' && value.length > 0);
    });
  });
}

function hasInAppExceptionFrame(properties: Record<string, unknown>): boolean {
  const list = properties.$exception_list;
  if (!Array.isArray(list)) {
    return false;
  }

  return list.some((entry) => {
    if (!entry || typeof entry !== 'object') {
      return false;
    }

    const stacktrace = (entry as { stacktrace?: unknown }).stacktrace;
    if (!stacktrace || typeof stacktrace !== 'object') {
      return false;
    }

    const frames = (stacktrace as { frames?: unknown }).frames;
    if (!Array.isArray(frames)) {
      return false;
    }

    return frames.some((frame) => {
      if (!frame || typeof frame !== 'object') {
        return false;
      }

      const record = frame as Record<string, unknown>;
      if (record.in_app === true) {
        return true;
      }

      const junkDrawer = record.junk_drawer;
      if (!junkDrawer || typeof junkDrawer !== 'object') {
        return false;
      }

      const rawFrame = (junkDrawer as { raw_frame?: unknown }).raw_frame;
      return Boolean(
        rawFrame &&
          typeof rawFrame === 'object' &&
          'in_app' in rawFrame &&
          (rawFrame as { in_app?: unknown }).in_app === true,
      );
    });
  });
}

function isChunkLoadFailureMessage(value: string): boolean {
  return CHUNK_LOAD_FAILURE_MESSAGE_PATTERN.test(value);
}

function isLocalDevelopmentUrl(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }

  try {
    const parsed = new URL(value);
    return (
      parsed.hostname === 'localhost' ||
      parsed.hostname === '127.0.0.1' ||
      parsed.hostname === '::1' ||
      parsed.hostname === '[::1]'
    );
  } catch {
    return /^https?:\/\/(?:localhost|127\.0\.0\.1|\[::1\])(?::\d+)?(?:\/|$)/i.test(value);
  }
}

function isPostHogTransportFrame(value: string): boolean {
  return (
    value.includes('/node_modules/.vite/deps/') ||
    /(?:posthog|retriableRequest|_send_request)/i.test(value)
  );
}

function isLocalPostHogTransportFetchFailure(properties: Record<string, unknown>): boolean {
  const values = exceptionValues(properties);
  if (
    !exceptionTypes(properties).includes('TypeError') ||
    values.length === 0 ||
    !values.every((value) => value === 'Failed to fetch') ||
    !isLocalDevelopmentUrl(properties.$current_url)
  ) {
    return false;
  }

  return exceptionFrameValues(properties).some(isPostHogTransportFrame);
}

function isContextFreeBrowserNetworkFailure(properties: Record<string, unknown>): boolean {
  const types = exceptionTypes(properties);
  const values = exceptionValues(properties);

  return (
    types.length > 0 &&
    types.every((type) => type === 'TypeError') &&
    values.length > 0 &&
    values.every((value) => RAW_BROWSER_NETWORK_FAILURE_MESSAGES.has(value)) &&
    !STRUCTURED_EXCEPTION_CONTEXT_KEYS.some((key) => properties[key] !== undefined) &&
    !hasInAppExceptionFrame(properties)
  );
}

function extractChunkAssetMetadata(message: string): Record<string, unknown> {
  const match = message.match(URL_IN_TEXT_PATTERN);
  const assetUrl = match?.[0];
  if (!assetUrl) {
    return {};
  }

  if (assetUrl.startsWith('/')) {
    const chunkAssetPath = assetUrl.split(/[?#]/)[0] ?? assetUrl;
    const origin =
      typeof window !== 'undefined' && typeof window.location?.origin === 'string'
        ? window.location.origin
        : null;

    if (!origin) {
      return {
        chunk_asset_path: chunkAssetPath,
        chunk_asset_url: chunkAssetPath,
      };
    }

    try {
      const parsed = new URL(assetUrl, origin);
      return {
        chunk_asset_host: parsed.host,
        chunk_asset_path: parsed.pathname,
        chunk_asset_url: `${parsed.origin}${parsed.pathname}`,
      };
    } catch {
      return {
        chunk_asset_path: chunkAssetPath,
        chunk_asset_url: chunkAssetPath,
      };
    }
  }

  try {
    const parsed = new URL(assetUrl);
    return {
      chunk_asset_host: parsed.host,
      chunk_asset_path: parsed.pathname,
      chunk_asset_url: `${parsed.origin}${parsed.pathname}`,
    };
  } catch {
    return {
      chunk_asset_url: assetUrl,
    };
  }
}

function buildExceptionEventDiagnostics(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const chunkLoadFailure = exceptionValues(properties).find(isChunkLoadFailureMessage);
  if (!chunkLoadFailure) {
    return {};
  }

  return {
    code: properties.code ?? 'chunk_load_failed',
    error_category: properties.error_category ?? 'network',
    failure_phase: properties.failure_phase ?? 'chunk_load',
    step: properties.step ?? 'load_lazy_chunk',
    surface: properties.surface ?? 'app_shell',
    user_visible_message:
      properties.user_visible_message ??
      'A lazy-loaded app asset failed to load. This is usually a stale deployed chunk, cache mismatch, or blocked network request.',
    ...extractChunkAssetMetadata(chunkLoadFailure),
  };
}

function shouldDropClientPostHogEvent(event: CaptureResult): boolean {
  if (event.event === 'app_error' && event.properties.code === 'auth_required') {
    return true;
  }

  if (event.event !== '$exception') {
    return false;
  }

  const properties = event.properties as Record<string, unknown>;
  const types = exceptionTypes(properties);
  if (types.includes('ZodError')) {
    return true;
  }

  if (isLocalPostHogTransportFetchFailure(properties)) {
    return true;
  }

  if (isContextFreeBrowserNetworkFailure(properties)) {
    return true;
  }

  const isFetchJsonAuthError =
    types.includes('FetchJsonError') &&
    (properties.code === 'auth_required' ||
      properties.http_status === 401 ||
      exceptionValues(properties).includes(AUTH_REQUIRED_MESSAGE));

  return isFetchJsonAuthError;
}

export function prepareClientPostHogEvent(
  event: CaptureResult | null,
  settings: Pick<ClientPostHogSettings, 'environment'>,
): CaptureResult | null {
  if (!event) {
    return null;
  }

  const eventProperties = (event.properties ?? {}) as Record<string, unknown>;
  const withContext: CaptureResult = {
    ...event,
    properties: stripUndefinedValues({
      ...eventProperties,
      ...buildExceptionEventDiagnostics(eventProperties),
      environment: settings.environment,
      source: 'client',
    }) as CaptureResult['properties'],
  };

  return shouldDropClientPostHogEvent(withContext) ? null : withContext;
}

export function resolveClientPostHogSettings(
  env: ClientPostHogEnv = import.meta.env,
): ClientPostHogSettings {
  const key = normalizeString(env.VITE_PUBLIC_POSTHOG_KEY);

  return {
    defaults: POSTHOG_SDK_DEFAULTS,
    enabled: Boolean(key),
    environment: resolveClientAnalyticsEnvironment(env),
    host: normalizeString(env.VITE_PUBLIC_POSTHOG_HOST) ?? DEFAULT_POSTHOG_HOST,
    key,
    uiHost: DEFAULT_POSTHOG_UI_HOST,
  };
}

export function initClientPostHog(env: ClientPostHogEnv = import.meta.env): ClientPostHogSettings {
  const settings = resolveClientPostHogSettings(env);

  if (!settings.enabled || !settings.key || isPostHogInitialized) {
    return settings;
  }

  posthog.init(settings.key, {
    api_host: settings.host,
    autocapture: false,
    capture_exceptions: {
      capture_console_errors: true,
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
    },
    capture_pageleave: false,
    capture_pageview: 'history_change',
    defaults: settings.defaults,
    debug: env.DEV === true,
    before_send: (event) => prepareClientPostHogEvent(event, settings),
    person_profiles: 'identified_only',
    ui_host: settings.uiHost,
  });

  posthog.register({
    environment: settings.environment,
    source: 'client',
  });

  isPostHogInitialized = true;

  return settings;
}

export function identifyPostHogUser(
  distinctId: string,
  properties: Record<string, unknown> = {},
): void {
  if (!isPostHogInitialized) {
    return;
  }

  posthog.identify(distinctId, properties);
}

export function resetPostHogUser(): void {
  if (!isPostHogInitialized) {
    return;
  }

  posthog.reset();
}

export function capturePostHogEvent(event: string, properties: Record<string, unknown> = {}): void {
  if (!isPostHogInitialized) {
    return;
  }

  posthog.capture(event, stripUndefinedValues(properties));
}

export function captureClientError(error: unknown, options: CaptureClientErrorOptions = {}): void {
  if (shouldSkipErrorCapture(error)) {
    return;
  }

  if (!isPostHogInitialized) {
    // Keep errors visible locally even when analytics is not configured.
    console.error(error);
    return;
  }

  posthog.captureException(toError(error), {
    ...options.extras,
    ...Object.fromEntries(
      Object.entries(options.tags ?? {}).flatMap(([key, value]) =>
        value === undefined ? [] : [[`tag_${key}`, value]],
      ),
    ),
  });
}

export function buildClientAppErrorProperties(
  error: unknown,
  context: AppErrorContext & Pick<AppErrorReport, 'details'>,
): Record<string, unknown> {
  const exception = toError(error);
  const envelope = getAppErrorEnvelope(error);
  return normalizeAppErrorReport({
    ...context,
    code: context.code ?? envelope?.code ?? getErrorCode(error),
    details: context.details ?? envelope?.details ?? getErrorDetails(error),
    error_category: context.error_category ?? getFetchJsonErrorCategory(error),
    exception_message: exception.message,
    failure_phase: context.failure_phase ?? getErrorString(error, 'failurePhase'),
    http_status:
      typeof (error as { status?: unknown })?.status === 'number'
        ? ((error as { status: number }).status as number)
        : context.http_status,
    is_retryable: envelope?.retryable ?? context.is_retryable ?? getErrorRetryable(error),
    network_error_message:
      context.network_error_message ?? getErrorString(error, 'networkErrorMessage'),
    network_error_name: context.network_error_name ?? getErrorString(error, 'networkErrorName'),
    request_duration_ms: context.request_duration_ms ?? getErrorNumber(error, 'requestDurationMs'),
    request_id: envelope?.requestId ?? context.request_id ?? getErrorRequestId(error),
    request_method: context.request_method ?? getErrorString(error, 'requestMethod'),
    request_path: context.request_path ?? getErrorString(error, 'requestPath'),
    request_query_keys:
      context.request_query_keys ?? getErrorStringArray(error, 'requestQueryKeys'),
    request_url: context.request_url ?? getErrorString(error, 'requestUrl'),
    response_body_kind: context.response_body_kind ?? getErrorString(error, 'responseBodyKind'),
    response_body_preview:
      context.response_body_preview ?? getErrorString(error, 'responseBodyPreview'),
    response_content_type:
      context.response_content_type ?? getErrorString(error, 'responseContentType'),
    user_visible_message: context.user_visible_message ?? envelope?.message ?? exception.message,
  });
}

export function captureAppError(
  error: unknown,
  context: AppErrorContext & Pick<AppErrorReport, 'details'>,
): void {
  if (shouldSkipErrorCapture(error)) {
    return;
  }

  const exception = toError(error);
  const properties = buildClientAppErrorProperties(error, context);
  const envelope = getAppErrorEnvelope(error);
  const stableCode = context.code ?? envelope?.code ?? getErrorCode(error);
  const fingerprint = buildClientExceptionFingerprint({
    code: stableCode,
    error_category:
      context.error_category ??
      getFetchJsonErrorCategory(error) ??
      (stableCode ? classifyAppErrorCategory({ code: stableCode }) : undefined),
    http_status: getErrorStatus(error) ?? context.http_status,
    request_path: context.request_path ?? getErrorString(error, 'requestPath'),
    step: context.step,
    surface: context.surface,
  });

  if (!isPostHogInitialized) {
    console.error(exception);
    return;
  }

  posthog.captureException(
    exception,
    fingerprint ? { ...properties, $exception_fingerprint: fingerprint } : properties,
  );
  posthog.capture('app_error', properties);
}

export default posthog;
